/**
 * Markdown + Mermaid renderer configuration
 *
 * Configures `marked` with GFM, heading IDs, mermaid code-block rendering
 * (via beautiful-mermaid) and ark-diagram iframes.
 */

import { marked } from 'marked'
import { renderMermaidSVG, THEMES } from 'beautiful-mermaid'
import { WAVE_API } from '../config'

const mermaidColors = { ...THEMES['github-light'], font: 'Roboto, sans-serif' }

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

marked.use({
  breaks: true,
  gfm: true,
  renderer: {
    heading({ text, depth }: { text: string; depth: number }) {
      const id = slugify(text)
      return `<h${depth} id="${id}">${text}</h${depth}>`
    },
    code({ text, lang }: { text: string; lang?: string | undefined }) {
      if (lang === 'mermaid') {
        const decoded = text
          .replace(/&amp;/g, '&')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&quot;/g, '"')
        // beautiful-mermaid (v1.1.3) only handles `flowchart`/`graph`
        // and `stateDiagram`. Anything else (erDiagram, sequenceDiagram,
        // classDiagram, gantt, …) gets a placeholder div that the
        // `useMermaidAsync` hook fills in with the official `mermaid`
        // library after mount. Without this, erDiagram entity-attribute
        // blocks were lost — Alex saw empty boxes + leaked text below.
        const firstLine = decoded.trim().split('\n', 1)[0]?.trim().toLowerCase() || ''
        const beautifulHandles =
          firstLine.startsWith('graph') ||
          firstLine.startsWith('flowchart') ||
          firstLine.startsWith('statediagram')
        if (beautifulHandles) {
          try {
            const svg = renderMermaidSVG(decoded, mermaidColors)
            return `<div class="mermaid-rendered">${svg}</div>`
          } catch {
            return `<pre class="mermaid-error"><code>${text}</code></pre>`
          }
        }
        const escaped = decoded
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
        return `<div class="mermaid-async" data-mermaid-source="${escaped}"></div>`
      }
      // D2 (d2lang.com) — state-of-art text-to-diagram language with
      // ELK auto-layout. Same async-placeholder pattern as the mermaid
      // fallback — `useD2Async` post-processes after mount.
      if (lang === 'd2') {
        const decoded = text
          .replace(/&amp;/g, '&')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&quot;/g, '"')
        const escaped = decoded
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
        return `<div class="d2-async" data-d2-source="${escaped}"></div>`
      }
      if (lang === 'ark') {
        const decoded = text
          .replace(/&amp;/g, '&')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&quot;/g, '"')
        const config = btoa(unescape(encodeURIComponent(decoded)))
        const instanceId = Math.random().toString(36).slice(2, 8)
        if (!WAVE_API) {
          return `<div class="ark-diagram ark-diagram--unavailable"><em>Diagramm-Einbettung ist auf dieser Instanz nicht konfiguriert (VITE_WAVE_API).</em></div>`
        }
        return `<div class="ark-diagram"><iframe src="${WAVE_API}/ark?embed=true&instance=${instanceId}&config=${config}" width="100%" height="500" frameborder="0" allowfullscreen loading="lazy"></iframe></div>`
      }
      return false as unknown as string
    },
  },
})

export function parseMarkdown(content: string): string {
  return marked(content) as string
}
